import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import BubbleOrb from '@/components/BubbleOrb';

import { bubbleImageSrc } from '@/lib/bubbleImage';
const ClaimPass = () => {
  const { token } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const [landed, setLanded] = useState(null); // bubble after claim

  useEffect(() => {
    if (!user) {
      localStorage.setItem('bx_pending_claim', token);
    }
    // Show the handwritten "TAKE CARE OF ME" note while the recipient
    // decides — sets emotional stakes before they tap accept.
    api.get(`/passes/${token}/touch-note`)
       .then(({ data }) => setNote(data))
       .catch(() => {});
  }, [user, token]);

  const claim = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/bubbles/claim', { pass_token: token });
      // Synced haptic — backend has already broadcast touch_sync at this moment
      if (navigator.vibrate) {
        try { navigator.vibrate([0, 30, 60, 30, 200, 50, 300]); } catch {}
      }
      setLanded(data.bubble);
      localStorage.removeItem('bx_pending_claim');
      toast.success(`You received ${data.bubble.name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'cannot claim');
    } finally { setBusy(false); }
  };

  if (landed) {
    return (
      <div className="min-h-screen grid place-items-center px-4 py-10" data-testid="claim-landed">
        <motion.div className="max-w-md w-full text-center"
                    initial={{ scale: 0.7, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: 'spring', stiffness: 140 }}>
          {bubbleImageSrc(landed) ? (
            <img src={bubbleImageSrc(landed)}
                 alt={landed.name}
                 className="w-56 h-56 object-cover rounded-full mx-auto ring-2 ring-bx-cyan/40 breathe" />
          ) : (
            <BubbleOrb rarity={landed.rarity} size="lg" />
          )}
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-7">
            <span className="shimmer-text">{landed.name}</span> just landed
          </h1>
          <p className="text-white/65 mt-3 italic">"{landed.personality?.bio}"</p>
          <button onClick={() => nav(`/bubble/${landed.id}`)} data-testid="claim-go-bubble"
                  className="btn-primary px-7 py-3.5 rounded-full text-sm mt-8 inline-flex items-center gap-2">
            Open my bubble →
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center px-4 py-10">
      <div className="max-w-md w-full text-center">
        <BubbleOrb rarity="legendary" size="lg" />
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-6">
          Someone passed you <span className="shimmer-text">energy</span>
        </h1>
        <p className="text-white/65 mt-3">
          A Bubble is on its way to you. It even left you a note.
        </p>

        {note?.note_image_b64 && (
          <motion.div initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.2 }}
                      className="mx-auto my-7 max-w-xs"
                      data-testid="claim-note">
            <img src={`data:image/png;base64,${note.note_image_b64}`}
                 alt="handwritten note"
                 className="rounded-xl shadow-2xl rotate-[-1.5deg] ring-1 ring-white/10" />
            <div className="mt-3 text-[10px] uppercase tracking-[0.3em] text-bx-volt">
              a note from {note.bubble_name}
            </div>
          </motion.div>
        )}

        {user ? (
          <button data-testid="claim-accept" onClick={claim} disabled={busy}
                  className="btn-primary px-7 py-3.5 rounded-full text-sm inline-flex items-center gap-2 mt-2 disabled:opacity-50">
            {busy ? 'Receiving…' : 'Accept this Bubble'}
          </button>
        ) : (
          <div className="mt-8 space-y-3">
            <Link to="/register" data-testid="claim-register"
                  className="btn-primary block px-7 py-3.5 rounded-full text-sm">
              Claim invite to receive
            </Link>
            <Link to="/login" data-testid="claim-login" className="text-bx-cyan text-sm font-bold">
              Already a member? Sign in →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClaimPass;
